// App.jsx - Main Application Router (UI RESTORED to match original gigwave.in)
import './styles/rockTheme.css';
import bgImage from './assets/bg-gig-collage.jpg';

import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { GigProvider } from './contexts/GigContext';

// Import modals
import AuthModal from './components/modals/AuthModal';
import ForgotPasswordModal from './components/modals/ForgotPasswordModal';
import ProfileSetupModal from './components/modals/ProfileSetupModal';
import VenueProfileSetupModal from './components/modals/VenueProfileSetupModal';
import GigModal from './components/modals/GigModal';
import GigDetailModal from './components/modals/GigDetailModal';

// Import pages
import ArtistDashboard from './pages/ArtistDashboard';
import AudienceMode from './pages/AudienceMode';
import DiscoverMode from './pages/DiscoverMode';
import LiveMode from './pages/LiveMode';
import AdminDashboard from './pages/AdminDashboard';
import VenueDashboard from './pages/VenueDashboard';
import ArtistPublicPage from './pages/ArtistPublicPage';
import FollowButton from './components/FollowButton';
import PushPermissionPrompt from './components/PushPermissionPrompt';

// Import Firebase utilities
import {
  signOutUser,
  saveArtistProfile,
  getArtistProfile,
  publishArtistProfile,
  sendEmailVerification,
  checkEmailVerified,
  saveMasterPlaylist,
  getMasterPlaylist,
  saveGigPlaylists,
  getGigPlaylists,
  createLiveGig,
  listenToLiveGig,
  endLiveGig,
  extendGigTime,
  voteForSong,
  submitSongRequest,
  acceptSongRequest,
  rejectSongRequest,
  getUserLocation,
  searchNearbyGigs,
  calculateDistance,
  isWithinRange,
  getActiveLiveGigForArtist,
  updateGigToLive,
  getVenueProfile,
  saveVenueProfile,
} from './firebase-utils';

import { db, auth } from './firebase-utils';
import { doc, setDoc, getDoc, increment, onSnapshot } from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';

// ============================================
// PLACEHOLDER FUNCTIONS
// ============================================

const markSongAsPlayed = async (gigId, songId) => {
  console.log('markSongAsPlayed called:', gigId, songId);
  const gigRef = doc(db, 'liveGigs', gigId);
  await setDoc(gigRef, { playedSongs: increment(1) }, { merge: true });
};

const rateArtist = async (artistId, gigId, userId, rating) => {
  console.log('rateArtist called:', artistId, gigId, userId, rating);
  const ratingRef = doc(db, 'artistRatings', `${artistId}_${userId}_${gigId}`);
  await setDoc(ratingRef, { artistId, gigId, userId, rating, timestamp: Date.now() });
};

// ============================================
// DETECT PUBLIC ARTIST PROFILE URL
// gigwave.in/artist/:slug
// ============================================
const getPublicArtistSlug = () => {
  const path = window.location.pathname;
  const match = path.match(/^\/artist\/([a-z0-9-]+)$/i);
  return match ? match[1] : null;
};

// ============================================
// MAIN APP COMPONENT
// ============================================

function AppContent() {
  const { currentUser, userProfile, loading: authLoading } = useAuth();

  // ============================================
  // CORE STATE
  // ============================================

  const [mode, setMode] = useState('discover');

  // Check for public artist profile URL on first render
  const [publicArtistSlug] = useState(() => getPublicArtistSlug());

  // ============================================
  // MODAL STATE
  // ============================================

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showProfileSetup, setShowProfileSetup] = useState(false);
  const [showGigModal, setShowGigModal] = useState(false);
  const [showGigDetailModal, setShowGigDetailModal] = useState(false);

  // ============================================
  // AUTH STATE
  // ============================================

  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');

  // ============================================
  // PROFILE STATE
  // ============================================

  const [artistProfile, setArtistProfile] = useState(null);
  const [profileStep, setProfileStep] = useState(1);
  const [profileData, setProfileData] = useState({
    artistName: '',
    fullName: '',
    bio: '',
    profilePhoto: '',
    genre: '',
    location: '',
    socialMedia: {
      instagram: '',
      facebook: '',
      youtube: '',
      linkedin: '',
      twitter: '',
      tiktok: '',
      soundcloud: ''
    }
  });
  const [emailVerified, setEmailVerified] = useState(false);

  // ============================================
  // VENUE STATE
  // ============================================

  const [venueProfile, setVenueProfile] = useState(null);
  const [showVenueSetup, setShowVenueSetup] = useState(false);
  const [venueProfileStep, setVenueProfileStep] = useState(1);
  const [venueEmailVerified, setVenueEmailVerified] = useState(false);
  const [savingVenueProfile, setSavingVenueProfile] = useState(false);
  const [pendingVenueMode, setPendingVenueMode] = useState(false);
  const [venueProfileData, setVenueProfileData] = useState({
    venueName: '', venueType: '', capacity: '', location: '',
    description: '', genres: '', typicalBudget: '',
    phone: '', contactEmail: '', website: '',
    socialMedia: { instagram: '', facebook: '', linkedin: '' },
  });

  // ============================================
  // PLAYLIST STATE
  // ============================================

  const [masterSongs, setMasterSongs] = useState([]);
  const [gigPlaylists, setGigPlaylists] = useState([]);
  const [playlistSearchQuery, setPlaylistSearchQuery] = useState('');
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [showPlaylistModal, setShowPlaylistModal] = useState(false);

  // ============================================
  // GIG STATE
  // ============================================

  const [gigs, setGigs] = useState([]);
  const [editingGig, setEditingGig] = useState(null);
  const [savingGig, setSavingGig] = useState(false);
  const [capturingGPS, setCapturingGPS] = useState(false);

  // ============================================
  // LIVE GIG STATE
  // ============================================

  const [liveGig, setLiveGig] = useState(null);
  const [liveGigData, setLiveGigData] = useState({
    queuedSongs: [],
    playedSongs: [],
    votes: {},
    songRequests: [],
    audienceCount: 0,
    requestsEnabled: true,
    songLimit: 20
  });
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [liveTab, setLiveTab] = useState('queue');
  const [liveMasterSearch, setLiveMasterSearch] = useState('');
  const [isEndingGig, setIsEndingGig] = useState(false);
  const [showJukeboxSettings, setShowJukeboxSettings] = useState(false);
  const [requestsEnabled, setRequestsEnabled] = useState(true);

  // ============================================
  // DISCOVERY STATE
  // ============================================

  const [userLocation, setUserLocation] = useState(null);
  const [nearbyGigs, setNearbyGigs] = useState([]);
  const [loadingGigs, setLoadingGigs] = useState(false);
  const [dateFilter, setDateFilter] = useState('today');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredGigs, setFilteredGigs] = useState([]);
  const [selectedUpcomingGig, setSelectedUpcomingGig] = useState(null);

  // Artist search
  const [artistSearchQuery, setArtistSearchQuery] = useState('');
  const [artistSuggestions, setArtistSuggestions] = useState([]);
  const [loadingArtists, setLoadingArtists] = useState(false);
  const [showArtistSuggestions, setShowArtistSuggestions] = useState(false);
  const [showingArtistResults, setShowingArtistResults] = useState(false);

  // ============================================
  // AUDIENCE STATE
  // ============================================

  const [userVotes, setUserVotes] = useState({});
  const [hasRatedArtist, setHasRatedArtist] = useState(false);
  const [currentGigRating, setCurrentGigRating] = useState(0);
  const [artistRating, setArtistRating] = useState(0);
  const [selectedRequestSong, setSelectedRequestSong] = useState(null);
  const [requestMessage, setRequestMessage] = useState('');
  const [viewingArtistProfile, setViewingArtistProfile] = useState(null);
  const [interestedGigs, setInterestedGigs] = useState([]);

  // ============================================
  // BACKGROUND IMAGE
  // ============================================

  useEffect(() => {
    document.body.style.backgroundImage = `url(${bgImage})`;
    document.body.style.backgroundSize = 'cover';
    document.body.style.backgroundPosition = 'center';
    document.body.style.backgroundAttachment = 'fixed';
    document.body.style.backgroundRepeat = 'no-repeat';

    const overlay = document.createElement('div');
    overlay.id = 'bg-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0; left: 0;
      width: 100%; height: 100%;
      background: rgba(0, 0, 0, 0.01);
      z-index: -1;
      pointer-events: none;
    `;
    document.body.appendChild(overlay);

    return () => {
      document.body.style.backgroundImage = '';
      const el = document.getElementById('bg-overlay');
      if (el) el.remove();
    };
  }, []);

  // ============================================
  // VENUE MODE — check profile when entering
  // ============================================

  useEffect(() => {
    if (mode !== 'venue' || !currentUser) return;
    checkEmailVerified().then(v => setVenueEmailVerified(v));
    getVenueProfile(currentUser.uid).then(profile => {
      if (!profile || !profile.profileComplete) {
        if (profile) {
          setVenueProfileData({
            ...profile,
            socialMedia: profile.socialMedia || { instagram: '', facebook: '', linkedin: '' },
          });
        }
        setShowVenueSetup(true);
      } else {
        setVenueProfile(profile);
        setShowVenueSetup(false);
      }
    });
  }, [mode, currentUser]);

  // ============================================
  // PENDING VENUE MODE — redirect after login
  // ============================================

  useEffect(() => {
    if (currentUser && pendingVenueMode) {
      setPendingVenueMode(false);
      setMode('venue');
    }
  }, [currentUser, pendingVenueMode]);

  // ============================================
  // FORGOT PASSWORD HANDLER
  // ============================================

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetMessage('');
    if (!resetEmail) { setResetError('Please enter your email address'); return; }
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMessage('Password reset email sent! Check your inbox.');
      setTimeout(() => { setShowForgotPassword(false); setResetEmail(''); setResetMessage(''); }, 3000);
    } catch (error) {
      setResetError(error.message);
    }
  };

  // ============================================
  // PROFILE HANDLERS
  // ============================================

  const handleSaveProfile = async () => {
    try {
      await saveArtistProfile(currentUser.uid, profileData);
      await publishArtistProfile(currentUser.uid, profileData);
      setArtistProfile(profileData);
      setShowProfileSetup(false);
      alert('✅ Profile saved successfully!');
    } catch (error) {
      alert('Error saving profile: ' + error.message);
    }
  };

  const handleSendVerification = async () => {
    try {
      await sendEmailVerification();
      alert('✅ Verification email sent! Check your inbox.');
    } catch (error) {
      alert('Error sending verification: ' + error.message);
    }
  };

  const handleCheckVerification = async () => {
    try {
      const verified = await checkEmailVerified();
      setEmailVerified(verified);
      if (verified) { alert('✅ Email verified!'); }
      else { alert('❌ Email not verified yet. Please check your inbox.'); }
    } catch (error) {
      alert('Error checking verification: ' + error.message);
    }
  };

  // ============================================
  // VENUE PROFILE HANDLERS
  // ============================================

  const handleSaveVenueProfile = async () => {
    setSavingVenueProfile(true);
    try {
      await saveVenueProfile(currentUser.uid, {
        ...venueProfileData,
        userId: currentUser.uid,
        profileComplete: true,
      });
      setVenueProfile({ ...venueProfileData, userId: currentUser.uid, profileComplete: true });
      setShowVenueSetup(false);
      alert('✅ Venue profile saved!');
    } catch (err) { alert('Error: ' + err.message); }
    finally { setSavingVenueProfile(false); }
  };

  const handleVenueSendVerification = async () => {
    try {
      await sendEmailVerification();
      alert('✅ Verification email sent! Check your inbox.');
    } catch (error) { alert(error.message); }
  };

  const handleVenueCheckVerification = async () => {
    try {
      const verified = await checkEmailVerified();
      setVenueEmailVerified(verified);
      if (verified) alert('✅ Email verified!');
      else alert('❌ Not verified yet. Check your inbox.');
    } catch (error) { alert(error.message); }
  };

  // ============================================
  // GIG HANDLERS
  // ============================================

  const captureGPSLocation = async () => {
    setCapturingGPS(true);
    try {
      const location = await getUserLocation();
      setEditingGig({ ...editingGig, location, address: `${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}` });
      alert('✅ GPS location captured!');
    } catch (error) {
      alert('❌ Error capturing location: ' + error.message);
    } finally {
      setCapturingGPS(false);
    }
  };

  const saveGig = async () => {
    setSavingGig(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      alert('✅ Gig saved!');
      setShowGigModal(false);
    } catch (error) {
      alert('Error saving gig: ' + error.message);
    } finally {
      setSavingGig(false);
    }
  };

  // ============================================
  // DISCOVERY HANDLERS
  // ============================================

  const handleSearchGigs = async () => {
    setLoadingGigs(true);
    try {
      const location = await getUserLocation();
      setUserLocation(location);
      const gigs = await searchNearbyGigs(location, 1000);
      setNearbyGigs(gigs);
      setFilteredGigs(gigs);
    } catch (error) {
      alert('Error searching gigs: ' + error.message);
    } finally {
      setLoadingGigs(false);
    }
  };

  const handleJoinGig = async (gig) => {
    if (!currentUser) { setShowAuthModal(true); return; }
    if (!userLocation) {
      const location = await getUserLocation();
      setUserLocation(location);
    }
    const distance = calculateDistance(userLocation, gig.location);
    if (distance > 1) { alert('❌ You must be within 1km of the venue to join this gig!'); return; }
    setLiveGig(gig);
    setMode('audience');
  };

  const handleViewArtistProfile = async (artistId) => {
    try {
      const profile = await getArtistProfile(artistId);
      setViewingArtistProfile(profile);
    } catch (error) {
      alert('Error loading artist profile: ' + error.message);
    }
  };

  const handleCloseArtistProfile = () => setViewingArtistProfile(null);

  const handleArtistSearch = async (query) => {
    setLoadingArtists(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      setArtistSuggestions([]);
    } catch (error) {
      console.error('Error searching artists:', error);
    } finally {
      setLoadingArtists(false);
    }
  };

  const calculateGigStatus = (gig) => {
    const now = new Date();
    const gigTime = new Date(gig.date + 'T' + gig.time);
    const endTime = new Date(gigTime.getTime() + (gig.duration || 180) * 60000);
    if (now >= gigTime && now <= endTime) return 'live';
    if (now < gigTime) return 'upcoming';
    return 'ended';
  };

  // ============================================
  // LIVE MODE HANDLERS
  // ============================================

  const handleGoLive = async (gig) => {
    if (liveGig) {
      alert('⚠️ You are already live! End your current gig first.');
      setMode('live');
      return;
    }
    if (gig.status === 'ended') {
      alert('❌ This gig has ended! You cannot go live with an ended gig.');
      return;
    }

    try {
      const gigData = {
        artistId: currentUser.uid,
        artistName: currentUser.displayName || 'Artist',
        venueName: gig.venueName,
        venueAddress: gig.address || '',
        location: gig.location,
        queuedSongs: gig.queuedSongs || [],
        maxQueueSize: gig.queueSize || 20,
        masterPlaylist: gig.masterPlaylist || [],
        status: 'live',
        gigDate: gig.date || gig.gigDate,
        gigTime: gig.time || gig.gigTime,
        gigId: gig.id,
      };

      let gigId;
      if (gig.id && typeof gig.id === 'string' && gig.id.length > 10) {
        gigId = await updateGigToLive(gig.id, gigData.queuedSongs, gigData.masterPlaylist, gigData.maxQueueSize || 20);
      } else {
        gigId = await createLiveGig(gigData, currentUser.uid);
      }

      const liveGigObj = { ...gigData, id: gigId };
      setLiveGig(liveGigObj);
      setMode('live');

      if (currentUser) {
        localStorage.setItem(`GigWave_live_${currentUser.uid}`, JSON.stringify(liveGigObj));
      }

      alert(`✅ You're live at ${gig.venueName}!\n\nAudience can find you by searching nearby!`);
    } catch (error) {
      alert('Error going live: ' + error.message);
    }
  };

  const handleEndGig = async (customMessage, skipConfirm) => {
    if (!skipConfirm && !window.confirm('Are you sure you want to end this gig?')) return;
    setIsEndingGig(true);
    try {
      await endLiveGig(liveGig.id, customMessage);
      setLiveGig(null);
      setMode('artist');
      if (currentUser) localStorage.removeItem(`GigWave_live_${currentUser.uid}`);
      alert('✅ Gig ended successfully!');
    } catch (error) {
      alert('Error ending gig: ' + error.message);
    } finally {
      setIsEndingGig(false);
    }
  };

  const handleExtendTime = async () => {
    try {
      await extendGigTime(liveGig.id);
      alert('✅ Added 1 hour to gig!');
    } catch (error) {
      alert('Error extending time: ' + error.message);
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try { await acceptSongRequest(liveGig.id, requestId); alert('✅ Request accepted!'); }
    catch (error) { alert('Error accepting request: ' + error.message); }
  };

  const handleRejectRequest = async (requestId) => {
    try { await rejectSongRequest(liveGig.id, requestId); alert('✅ Request rejected'); }
    catch (error) { alert('Error rejecting request: ' + error.message); }
  };

  const handleMarkAsPlayed = async (songId) => {
    try { await markSongAsPlayed(liveGig.id, songId); }
    catch (error) { alert('Error marking song as played: ' + error.message); }
  };

  const handleAddSongToQueue = async (songId) => {
    try { alert('✅ Song added to queue!'); }
    catch (error) { alert('Error adding song: ' + error.message); }
  };

  const sortArtistQueue = (songs) => {
    return songs.sort((a, b) => {
      const aVotes = liveGigData.votes[Math.floor(a.id)] || 0;
      const bVotes = liveGigData.votes[Math.floor(b.id)] || 0;
      const aRequested = a.isRequested ? 1 : 0;
      const bRequested = b.isRequested ? 1 : 0;
      if (bRequested !== aRequested) return bRequested - aRequested;
      return bVotes - aVotes;
    });
  };

  // ============================================
  // AUDIENCE HANDLERS
  // ============================================

  const handleVote = async (songId) => {
    if (!currentUser) { setShowAuthModal(true); return; }
    if (userVotes[songId]) { alert('❌ You already voted for this song!'); return; }
    try {
      await voteForSong(liveGig.id, songId, currentUser.uid);
      setUserVotes({ ...userVotes, [songId]: true });
    } catch (error) {
      alert('Error voting: ' + error.message);
    }
  };

  const handleSongRequest = async () => {
    if (!selectedRequestSong) { alert('❌ Please select a song first!'); return; }
    try {
      await submitSongRequest(liveGig.id, selectedRequestSong.id, currentUser.uid, currentUser.displayName || 'Anonymous', requestMessage);
      setSelectedRequestSong(null);
      setRequestMessage('');
      alert('✅ Request sent to artist!');
    } catch (error) {
      alert('Error submitting request: ' + error.message);
    }
  };

  const handleSubmitRating = async () => {
    if (artistRating === 0) { alert('❌ Please select a rating!'); return; }
    try {
      await rateArtist(liveGig.artistId, liveGig.id, currentUser.uid, artistRating);
      setHasRatedArtist(true);
      setCurrentGigRating(artistRating);
      alert(`✅ Rated ${artistRating} stars!`);
    } catch (error) {
      alert('Error submitting rating: ' + error.message);
    }
  };

  const handleCloseRating = () => setArtistRating(0);

  // ============================================
  // EFFECTS
  // ============================================

  useEffect(() => {
    if (liveGig) {
      const unsubscribe = listenToLiveGig(liveGig.id, (data) => {
        setLiveGigData(data);
        if (data.endTime) {
          const now = Date.now();
          const remaining = Math.max(0, Math.floor((data.endTime - now) / 1000));
          setTimeRemaining(remaining);
        }
      });
      return () => unsubscribe();
    }
  }, [liveGig]);

  // ============================================
  // RESTORE LIVE GIG ON REFRESH
  // ============================================
  useEffect(() => {
    if (!currentUser) return;

    const restoreLiveGig = async () => {
      try {
        const activeLiveGig = await getActiveLiveGigForArtist(currentUser.uid);

        if (activeLiveGig && activeLiveGig.status === 'live') {
          let location = activeLiveGig.location;
          if (location && location.latitude != null) {
            location = { lat: location.latitude, lng: location.longitude };
          }
          setLiveGig({ ...activeLiveGig, location });
          setMode('live');
          localStorage.setItem(`GigWave_live_${currentUser.uid}`, JSON.stringify({ ...activeLiveGig, _role: 'artist' }));
          return;
        }

        const saved = localStorage.getItem(`GigWave_live_${currentUser.uid}`);
        if (saved) {
          try {
            const savedGig = JSON.parse(saved);
            if (savedGig?.id) {
              const gigRef = doc(db, 'liveGigs', String(savedGig.id));
              const gigSnap = await getDoc(gigRef);
              if (gigSnap.exists() && gigSnap.data().status === 'live') {
                const gigData = gigSnap.data();
                let location = gigData.location;
                if (location && location.latitude != null) {
                  location = { lat: location.latitude, lng: location.longitude };
                }
                let restoreMode;
                if (savedGig._role === 'audience') {
                  restoreMode = 'audience';
                } else if (savedGig._role === 'artist') {
                  restoreMode = 'live';
                } else {
                  if (gigData.artistId === currentUser.uid) {
                    restoreMode = 'live';
                  } else {
                    localStorage.removeItem(`GigWave_live_${currentUser.uid}`);
                    return;
                  }
                }
                setLiveGig({ ...gigData, id: savedGig.id, location });
                setMode(restoreMode);
                return;
              }
            }
          } catch (e) {
            console.warn('Could not parse saved live gig:', e);
          }
          localStorage.removeItem(`GigWave_live_${currentUser.uid}`);
        }
      } catch (err) {
        console.warn('Error restoring live gig:', err);
      }
    };

    restoreLiveGig();
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      getArtistProfile(currentUser.uid).then(profile => {
        if (profile) { setArtistProfile(profile); setProfileData(profile); }
      });
    }
  }, [currentUser]);

  // ============================================
  // RENDER
  // ============================================

  // ── PUBLIC ARTIST PROFILE — no auth required ──
  // Renders when URL is /artist/:slug, before any auth check
  if (publicArtistSlug) {
    return (
      <ArtistPublicPage
        slug={publicArtistSlug}
        onBack={() => {
          window.history.pushState({}, '', '/');
          window.location.reload();
        }}
      />
    );
  }

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 flex items-center justify-center">
        <div className="text-center text-white">
          <h1 className="text-4xl font-bold" data-testid="loading-screen">Loading GigWave...</h1>
        </div>
      </div>
    );
  }

  if (showForgotPassword) {
    return (
      <ForgotPasswordModal
        show={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        resetEmail={resetEmail}
        setResetEmail={setResetEmail}
        resetMessage={resetMessage}
        resetError={resetError}
        handleForgotPassword={handleForgotPassword}
      />
    );
  }

  if (showAuthModal && !currentUser) {
    return (
      <AuthModal
        onClose={() => setShowAuthModal(false)}
        onForgotPassword={() => { setShowAuthModal(false); setShowForgotPassword(true); }}
      />
    );
  }

  const checkProfileComplete = (profile) => {
    if (!profile) return false;
    return !!(profile.artistName && profile.fullName && profile.bio && profile.bio.length >= 50 && profile.genre && profile.location);
  };

  if (currentUser && artistProfile && !checkProfileComplete(artistProfile)) {
    return (
      <ProfileSetupModal
        show={true}
        onClose={() => {}}
        currentUser={currentUser}
        emailVerified={emailVerified}
        profileStep={profileStep}
        setProfileStep={setProfileStep}
        profileData={profileData}
        setProfileData={setProfileData}
        handleSaveProfile={handleSaveProfile}
        handleSendVerification={handleSendVerification}
        handleCheckVerification={handleCheckVerification}
      />
    );
  }

  // ── Global modals ──
  const modals = (
    <>
      {showGigModal && (
        <GigModal
          show={showGigModal}
          onClose={() => setShowGigModal(false)}
          editingGig={editingGig}
          setEditingGig={setEditingGig}
          gigPlaylists={gigPlaylists}
          gigs={gigs}
          saveGig={saveGig}
          deleteGig={() => {}}
          savingGig={savingGig}
          captureGPSLocation={captureGPSLocation}
          capturingGPS={capturingGPS}
        />
      )}
      {showGigDetailModal && selectedUpcomingGig && (
        <GigDetailModal
          show={showGigDetailModal}
          onClose={() => setShowGigDetailModal(false)}
          selectedGig={selectedUpcomingGig}
          interestedGigs={interestedGigs}
          setInterestedGigs={setInterestedGigs}
          currentUser={currentUser}
          setShowAuthModal={setShowAuthModal}
        />
      )}
    </>
  );

  // ── Discovery Page ──
  if (mode === 'discover') {
    return (
      <>
        <DiscoverMode
          onModeChange={setMode}
          setLiveGig={setLiveGig}
          setShowAuthModal={setShowAuthModal}
          setSelectedUpcomingGig={setSelectedUpcomingGig}
          setShowGigDetailModal={setShowGigDetailModal}
          setUserVotes={setUserVotes}
          setHasRatedArtist={setHasRatedArtist}
          interestedGigs={interestedGigs}
          setInterestedGigs={setInterestedGigs}
          onVenuePortal={() => {
            if (!currentUser) { setPendingVenueMode(true); setShowAuthModal(true); }
            else setMode('venue');
          }}
          onJoinFromNotification={async (gigId, artistId) => {
            // User tapped "Join Live Now" from a notification
            if (!currentUser) { setShowAuthModal(true); return; }
            try {
              const gigRef = doc(db, 'liveGigs', String(gigId));
              const gigSnap = await getDoc(gigRef);
              if (gigSnap.exists() && gigSnap.data().status === 'live') {
                setLiveGig({ id: gigSnap.id, ...gigSnap.data() });
                setMode('audience');
              } else {
                alert('This gig is no longer live.');
              }
            } catch (err) { alert('Error joining gig: ' + err.message); }
          }}
        />
        {showGigDetailModal && selectedUpcomingGig && (
          <GigDetailModal
            show={showGigDetailModal}
            onClose={() => { setShowGigDetailModal(false); setSelectedUpcomingGig(null); }}
            selectedGig={selectedUpcomingGig}
            interestedGigs={interestedGigs}
            setInterestedGigs={setInterestedGigs}
            currentUser={currentUser}
            setShowAuthModal={setShowAuthModal}
          />
        )}

        {/* Push notification permission prompt — shown 3s after login */}
        {currentUser && <PushPermissionPrompt userId={currentUser.uid} />}
      </>
    );
  }

  // ── Artist Dashboard ──
  if (mode === 'artist') {
    return (
      <>
        {modals}
        <ArtistDashboard onModeChange={setMode} onGoLive={handleGoLive} />
      </>
    );
  }

  // ── Venue Dashboard ──
  if (mode === 'venue') {
    return (
      <>
        <VenueProfileSetupModal
          show={showVenueSetup}
          currentUser={currentUser}
          emailVerified={venueEmailVerified}
          profileStep={venueProfileStep}
          setProfileStep={setVenueProfileStep}
          profileData={venueProfileData}
          setProfileData={setVenueProfileData}
          handleSaveProfile={handleSaveVenueProfile}
          handleSendVerification={handleVenueSendVerification}
          handleCheckVerification={handleVenueCheckVerification}
          saving={savingVenueProfile}
        />
        <VenueDashboard onModeChange={setMode} initialVenueProfile={venueProfile} />
      </>
    );
  }

  // ── Audience Mode ──
  if (mode === 'audience' && liveGig) {
    return (
      <>
        {modals}
        <AudienceMode
          liveGig={liveGig}
          onModeChange={setMode}
          setShowAuthModal={setShowAuthModal}
          followButton={
            liveGig?.artistId ? (
              <FollowButton
                artistId={liveGig.artistId}
                artistName={liveGig.artistName}
                size="md"
                showCount={true}
                onAuthRequired={() => setShowAuthModal(true)}
              />
            ) : null
          }
        />
      </>
    );
  }

  // ── Live Mode ──
  if (mode === 'live' && liveGig) {
    return (
      <>
        {modals}
        <LiveMode
          liveGig={liveGig}
          liveGigData={liveGigData}
          setLiveGig={setLiveGig}
          timeRemaining={timeRemaining}
          liveTab={liveTab}
          setLiveTab={setLiveTab}
          liveMasterSearch={liveMasterSearch}
          setLiveMasterSearch={setLiveMasterSearch}
          showJukeboxSettings={showJukeboxSettings}
          setShowJukeboxSettings={setShowJukeboxSettings}
          requestsEnabled={requestsEnabled}
          setRequestsEnabled={setRequestsEnabled}
          isEndingGig={isEndingGig}
          handleEndGig={handleEndGig}
          handleExtendTime={handleExtendTime}
          handleAcceptRequest={handleAcceptRequest}
          handleRejectRequest={handleRejectRequest}
          handleMarkAsPlayed={handleMarkAsPlayed}
          handleAddSongToQueue={handleAddSongToQueue}
          sortArtistQueue={sortArtistQueue}
          setMode={setMode}
          onModeChange={setMode}
        />
      </>
    );
  }

  // ── Admin Dashboard ──
  if (mode === 'admin' && currentUser?.email === 'gig.wave.2005@gmail.com') {
    return (
      <AdminDashboard
        currentUser={currentUser}
        onClose={() => setMode('discover')}
      />
    );
  }

  // ── Fallback ──
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 flex items-center justify-center p-4">
      <div className="text-center text-white">
        <h1 className="text-6xl font-bold mb-4">GigWave</h1>
        <p className="text-xl mb-8">Live Performance Platform</p>
        <button onClick={() => setMode('discover')} data-testid="get-started-btn" className="px-12 py-6 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-bold text-2xl">
          Get Started
        </button>
      </div>
    </div>
  );
}

// ============================================
// APP WITH PROVIDERS
// ============================================

function App() {
  return (
    <AuthProvider>
      <GigProvider>
        <AppContent />
      </GigProvider>
    </AuthProvider>
  );
}

export default App;
